import { UIElement } from "../../common_ui_elements/ui_element.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { Vector2 } from "../../../common/vector2.js";

export type JumpDirection = "down" | "up";

const PILL_COLOR = "#0A1E60";
const TEXT_COLOR = "#C9D2F2";
const ARROW_COLOR = "#FFFFFF";

/** Rounded white triangle, pointing down or up. */
class JumpArrow extends UIElement
{
  private direction: JumpDirection = "down";
  private dom_last_key = "";

  public setDirection( direction: JumpDirection ): void
  {
    if ( direction === this.direction ) return;
    this.direction = direction;
    this.setShouldBeRendered();
  }

  protected override syncDomContent( _node: HTMLElement ): boolean
  {
    if ( this.dom_last_key === this.direction ) return true;
    this.dom_last_key = this.direction;
    const path = this.direction === "down" ? "M4 4 L46 4 L25 26 Z" : "M4 26 L46 26 L25 4 Z";
    this.getDomSkin().innerHTML =
      `<svg viewBox="0 0 50 30" width="100%" height="100%" preserveAspectRatio="none" style="display:block">`
      + `<path d="${path}" fill="${ARROW_COLOR}" stroke="${ARROW_COLOR}" stroke-width="7" stroke-linejoin="round"/></svg>`;
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    const w2 = this.rect.getW2();
    const h2 = this.rect.getH2();
    const down = this.direction === "down";
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo( -w2 * 0.84, down ? -h2 * 0.73 : h2 * 0.73 );
    ctx.lineTo( w2 * 0.84, down ? -h2 * 0.73 : h2 * 0.73 );
    ctx.lineTo( 0, down ? h2 * 0.73 : -h2 * 0.73 );
    ctx.closePath();
    ctx.fillStyle = ARROW_COLOR;
    ctx.strokeStyle = ARROW_COLOR;
    ctx.lineJoin = "round";
    ctx.lineWidth = this.rect.getW() * 0.14;
    ctx.fill();
    ctx.stroke();
    this.finishRender();
  }

  public name(): string { return "LeaderboardJumpArrow"; }
}

/**
 * "Down" / "Up" pill under the leaderboard list: jumps to the bottom of the board from its upper
 * half and back to the top from the lower half. Layout is in design px of the 1080x1920 frame.
 */
export class LeaderboardJumpButton extends UIElement
{
  private direction: JumpDirection = "down";
  private readonly arrow: JumpArrow;
  private readonly label: UITextElement;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, true );

    const pill = new RoundedRectangle( this.ctx, 0, 0, width, height, height / 2, PILL_COLOR );
    pill.setClickable( false );
    this.addChild( pill );

    const arrow_w = LayoutScaler.widthFromDesign( 54 );
    const arrow_h = LayoutScaler.heightFromDesign( 32 );
    this.arrow = new JumpArrow( this.ctx, LayoutScaler.widthFromDesign( 40 ), ( height - arrow_h ) / 2, arrow_w, arrow_h );
    this.arrow.setClickable( false );
    this.addChild( this.arrow );

    const text_x = LayoutScaler.widthFromDesign( 124 );
    this.label = new UITextElement( this.ctx, text_x, 0, width - text_x - LayoutScaler.widthFromDesign( 20 ), height, {
      localization_key: "LEADERBOARD.SCROLL_DOWN",
      text_color: TEXT_COLOR,
      text_style: TextStyleScheme.geologica,
      font_size: LayoutScaler.textSizeFromDesign( 46 ),
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 26 ),
      font_weight: 800,
      text_align: MyTextAlign.Left,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
    } );
    this.label.setClickable( false );
    this.addChild( this.label );
  }

  public getDirection(): JumpDirection
  {
    return this.direction;
  }

  /** Keep the small pill's touch target comfortable on phones. Coordinates are screen-local. */
  public containsTap( point: Vector2 ): boolean
  {
    return this.containsPointInner( this.toLocal( point ) );
  }

  protected override containsPointInner( point: Vector2 ): boolean
  {
    const w = this.rect.getW();
    const h = this.rect.getH();
    const pad_x = Math.max( 8, ( 44 - w ) / 2 );
    const pad_y = Math.max( 8, ( 44 - h ) / 2 );
    return point.x >= -pad_x && point.x <= w + pad_x
      && point.y >= -pad_y && point.y <= h + pad_y;
  }

  public setDirection( direction: JumpDirection ): void
  {
    if ( direction === this.direction ) return;
    this.direction = direction;
    this.arrow.setDirection( direction );
    this.label.setLocalizationKey( direction === "down" ? "LEADERBOARD.SCROLL_DOWN" : "LEADERBOARD.SCROLL_UP" );
    this.forceRedraw();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string { return "LeaderboardJumpButton"; }
}
